<script lang="ts" setup>
  import { computed, ref, type PropType, type Ref, watch } from 'vue';
  import { useBreakpoints, viewItemListEvent, viewPromotionEvent, selectPromotionEvent, useGoogleTagManager } from '@storefront/storefront';
  import { useShared } from '@othercommerce/foundation';
  import { UiModal } from '@storefront/components';
  import type { Brand, Section } from '@storefront/types';
  import type { ProductListingResource } from '@storefront/resources';

  interface Shared {
    productListMode: number;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    price: { type: Object, required: true },
    attributes: { type: Object, required: true },
    brands: { type: Array as PropType<Brand[]>, required: true },
    search: { type: String, required: false },
    options: { type: Object, required: true },
    categories: { type: Object, required: true },
    products: { type: Object, required: true },
    available: { type: Boolean, required: true },
    availableIn: { type: Array as PropType<number[]>, required: true },
    promotionsOnly: { type: Boolean, required: true },
    sections: { type: Array as PropType<Section[]>, required: true },
  });

  const breakpoints = useBreakpoints();
  const shared = useShared() as Ref<Shared>;
  const gtm = useGoogleTagManager();

  const areFiltersOpen = ref(false);
  const isProcessing = ref(false);

  const filters = computed(() => !breakpoints.value.lg ? UiModal : 'div');
  const hasProducts = computed(() => props.products.data.length > 0);
  const type = computed(() => shared.value.productListMode);

  function handleProcessingChange(newValue: boolean) {
    isProcessing.value = newValue;
  }

  watch(
    () => props.products.data,
    (products) => {
      gtm.push(viewItemListEvent(
        products,
        shared.value.activeCurrencyCode,
      ));
    },
    {
      immediate: true,
    },
  );

  watch(() => props.promotionsOnly, (value) => {
    if (value) {
      gtm.push(viewPromotionEvent({ header: 'Site-wide Promotion', placement: 'productsGrid', type: 'productsPromotionPage', products: props.products.data }));
    }
  }, { immediate: true });

  function onProductClick(product: ProductListingResource) {
    if (props.promotionsOnly) {
      gtm.push(selectPromotionEvent({ header: 'Site-wide Promotion', placement: 'productsGrid', type: 'productsPromotionPage' }, product));
    }
  }

</script>
<template>
  <div class="products">
    <component :is="filters" medium v-model:open="areFiltersOpen" id="filters-modal" class="products__filters-content" :aria-labelledby="!breakpoints.lg ? 'filters-modal-title': null">
      <SfModalHeader headingId="filters-modal-title" v-model:open="areFiltersOpen" v-if="!breakpoints.lg ">
        {{ $t('SHOP.COMMON.LABEL.FILTERS') }}
      </SfModalHeader>
      <ShopProductsFilter v-model:open="areFiltersOpen" id="filters" class="products__modal-filters" v-bind="{ categories, brands, price, attributes, options, search, available, availableIn, total: products.total }" />
    </component>
    <div class="products__content">

      <div v-if="sections?.length > 0" class="section-before">
        <SectionRenderer :sections="sections" placement="before" />
      </div>

      <ShopProductsGridControl id="products-view-option" @on-filters-open="areFiltersOpen = true" @processing-changed="handleProcessingChange" />
      <div v-if="hasProducts" id="products" :class="{ 'products__wrapper--list': type === 1 }" class="products__wrapper">
        <ProductCard v-for="product in products.data" :key="product.id" header="h3" :horizontal="type === 1" :product="product" class="products__product-card" :loading="isProcessing" @click="onProductClick(product)" />
      </div>
      <div v-else class="products__empty">
        <SfDataPlaceholder>{{ $t('SHOP.PRODUCT.MESSAGE.NO_PRODUCTS') }}</SfDataPlaceholder>
      </div>
      <SfPagination :pagination="products" />

      <div v-if="sections?.length > 0" class="section-after">
        <SectionRenderer :sections="sections" placement="after" />
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  @use '@storefront/styles/mixins/media';

  :root {
    --sf-products-columns: 4;
    --sf-products-columns-list: 1;
    --sf-products-padding: 1rem 1.5rem;

    @include media.query(xxl) {
      --sf-products-columns: 3;
    }

    @include media.query(xl) {
      --sf-products-columns: 2;
    }

    @include media.query(lg) {
      --sf-products-columns: 3;
    }

    @include media.query(md) {
      --sf-products-columns: 2;
    }

    @include media.query(sm) {
      --sf-products-columns: 1;
    }
  }
</style>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .products {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: var(--gap-default);
    width: 100%;

    @include media.query(lg) {
      flex-direction: column;
    }

    @media(min-width: 992px) {
      &__filters-content {
        width: 23rem;
        background: var(--section-background);
        border-inline: var(--section-border-inline);
        border-block: var(--section-border-block);
        border-radius: var(--section-border-radius);
        padding: var(--sf-products-padding);
        flex: 0 0 23rem;
      }
    }

    &__modal-filters {
      flex: 1;
    }

    &__content {
      width: 100%;
      min-width: 0;
    }

    &__wrapper,
    &__empty {
      margin-top: var(--margin-default);
      padding: var(--padding-card);
      background: var(--section-background);
      border-inline: var(--section-border-inline);
      border-block: var(--section-border-block);
      border-radius: var(--section-border-radius);

      @include media.query(lg) {
        border-radius: 0
      }
    }

    &__wrapper {
      position: relative;
      display: grid;
      grid-template-columns: repeat(var(--sf-products-columns), minmax(180px, 1fr));
      gap: var(--gap-card);
      background: var(--section-background);
      border-inline: var(--section-border-inline);
      border-block: var(--section-border-block);
      border-radius: var(--section-border-radius);

      &--list {
        grid-template-columns: var(--sf-products-columns-list);
      }
    }

    &__empty {
      background-color: var(--color-white);
      font-weight: bold;
      font-size: 1.5rem;
      text-align: center;
    }

    .section-before {
      width: 100%;
      padding-bottom: 1rem;
    }

    .section-after {
      width: 100%;
      padding-top: 1rem;
    }
  }
</style>
